import {
  ArrowDownTrayIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  LockClosedIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { QueryObserverResult, useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";

import { useCase } from "@/api/hooks/useCase.ts";
import { useFileUpload } from "@/api/hooks/useFileUpload";
import { useIsBuildingOwner } from "@/api/hooks/useIsBuildingOwner";
import { useUserStatus } from "@/api/hooks/useUser";
import { ApiResponse } from "@/api/rest/core/types";
import { UserInfo } from "@/api/rest/models/account";
import { HouseholdData } from "@/api/rest/models/household";
import { CaseMessageType, CaseStatus, CaseType } from "@/api/rest/models/warranty";
import { downloadDocument2, getDocumentUrl2 } from "@/api/rest/services/documentService";
import { fetchHouseholdData } from "@/api/rest/services/householdService";
import { markReplyHandled, sendFiles, sendMessage, updateStatus } from "@/api/rest/services/warrantyService";
import Button from "@/components/common/Button";
import LoadFailed from "@/components/common/LoadFailed";
import LoadingLogo from "@/components/common/LoadingLogo";
import { ChatMessage, ChatMessageHolder, ChatMessageType } from "@/components/common/ChatMessage.tsx";
import ChatVideo from "@/components/common/ChatVideo";
import ClosableBanner from "@/components/common/ClosableBanner";
import UploadStatus from "@/components/common/UploadStatus";
import UploadFilesPopup from "@/components/UploadFilesPopup.tsx";
import { CaseStatusDropdown } from "@/components/warranty-contents/CaseStatusDropdown";
import InspectionDefects from "@/components/warranty-contents/InspectionDefects";
import { parseCaseId } from "@/utils/caseId";
import { caseTypeNamespaceMap } from "@/utils/translation";

import { isOwnSideMessage } from "@/utils/chatUtils";
import { getAvailableHouseholdAccounts } from "@/utils/householdAccounts";

// Remount per case so drafts, pending files and local state never carry over to another case.
export default function CasePage({ type }: { type: CaseType }) {
  const caseId = parseCaseId(useParams<{ caseId: string }>().caseId);
  return caseId === null ? <CaseNotFound /> : <CaseView key={caseId} caseId={caseId} type={type} />;
}

function CaseNotFound() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col flex-1 items-center justify-center gap-4 p-8 text-center">
      <p className="text-lg">{t("casePage.notFound")}</p>
      <Button variant="outline" onClick={() => navigate("/")}>
        {t("casePage.backHome")}
      </Button>
    </div>
  );
}

function CaseView({ caseId, type }: { caseId: number; type: CaseType }) {
  const { t, i18n } = useTranslation();
  const namespace = caseTypeNamespaceMap.get(type);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [caseData, refreshCase, caseLoadFailed] = useCase(caseId);
  const { status: userStatus, retry: retryUser } = useUserStatus();
  const { user } = useOutletContext<{
    user: UserInfo;
    currentHousehold: {
      isLoading: boolean;
      data: {
        result: HouseholdData | undefined;
      };
      refetch: () => Promise<QueryObserverResult>;
    };
  }>();
  const currentHousehold = useQuery({
    queryKey: ["household", caseData?.case.householdId, i18n.language],
    queryFn: async () => {
      if (!caseData?.case.householdId) {
        return null;
      }
      const response = await fetchHouseholdData(caseData.case.householdId, i18n.language);
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch household data");
      }
      return response.result;
    },
    enabled: Boolean(caseData?.case.householdId),
  });

  const householdData = currentHousehold.data;
  const loading = currentHousehold.isLoading;

  const availableAccounts = useMemo(() => {
    return getAvailableHouseholdAccounts(user, householdData);
  }, [householdData, user]);
  const amIBuildingOwner = useIsBuildingOwner(user, householdData?.building);

  const messageContent = useRef<HTMLTextAreaElement>(null);

  const [isSending, setIsSending] = useState(false);
  const [hasMessage, setHasMessage] = useState(false);
  const [showTerminalReplyComposer, setShowTerminalReplyComposer] = useState(false);
  const [isMarkingHandled, setIsMarkingHandled] = useState(false);

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const { upload, progress: uploadProgress, error: sendError, setError: setSendError } = useFileUpload();
  const addFiles = useCallback(
    async (files: File[]): Promise<ApiResponse<unknown>> => {
      setPendingFiles([...pendingFiles, ...files]);
      return Promise.resolve({ success: true, result: null });
    },
    [pendingFiles],
  );

  useEffect(() => {
    setShowTerminalReplyComposer(searchParams.get("reply") === "1" || searchParams.get("reopen") === "1");
  }, [caseId, searchParams]);

  const openTerminalReplyComposer = useCallback(() => {
    setShowTerminalReplyComposer(true);
    if (searchParams.has("reply") || searchParams.has("reopen")) {
      const next = new URLSearchParams(searchParams);
      next.delete("reply");
      next.delete("reopen");
      setSearchParams(next, { replace: true });
    }
    requestAnimationFrame(() => messageContent.current?.focus());
  }, [searchParams, setSearchParams]);

  // Add this new combined function
  const submitFilesAndMessage = useCallback(async () => {
    if (isSending) {
      return;
    }

    // Get message content first (before any async operations)
    const message = messageContent.current?.value ?? "";
    const hasMessage = message.trim().length > 0;
    const hasFiles = pendingFiles.length > 0;

    if (!hasMessage && !hasFiles) {
      return; // Nothing to send
    }

    if (!availableAccounts || availableAccounts.length < 1) {
      return;
    }

    try {
      setIsSending(true);

      // Upload first: it is the slow part that can fail, and then nothing is sent and the text stays.
      const uploads = await upload(pendingFiles);
      if (!uploads) {
        return;
      }

      if (hasMessage) {
        const messageResponse = await sendMessage(caseId, {
          message: message,
          accountId: availableAccounts[0].id,
        });

        if (!messageResponse.success) {
          setSendError(messageResponse.message);
          return;
        }
        if (messageContent.current) {
          messageContent.current.value = "";
        }
        setHasMessage(false);
      }

      if (uploads.length > 0) {
        const filesResponse = await sendFiles(caseId, availableAccounts[0].id, uploads);

        if (filesResponse.success) {
          setPendingFiles([]);
        } else {
          setSendError(filesResponse.message);
        }
      }

      // Finally, refresh to show both updates
      await refreshCase();
    } finally {
      setIsSending(false);
    }
  }, [pendingFiles, caseId, availableAccounts, refreshCase, isSending, upload, setSendError]);

  const phoneNumber = useMemo(() => {
    if (!caseData?.messages?.[1]?.content) {
      return null;
    }

    const content = caseData.messages[1].content;
    const lastColonIndex = content.lastIndexOf(":");

    return lastColonIndex !== -1 ? content.substring(lastColonIndex + 1).trim() : null;
  }, [caseData]);

  const handleChangeStatus = useCallback(
    async (status: CaseStatus) => {
      if (!caseId) {
        return;
      }
      if (householdData?.building?.buildingId === undefined) {
        return;
      }
      const response = await updateStatus(caseId, {
        buildingId: householdData?.building?.buildingId,
        status: status,
      });
      if (response.success) {
        await refreshCase();
      }
    },
    [caseId, householdData, refreshCase],
  );

  const handleMarkReplyHandled = useCallback(async () => {
    if (!caseId || isMarkingHandled) {
      return;
    }
    try {
      setIsMarkingHandled(true);
      const response = await markReplyHandled(caseId);
      if (response.success) {
        await refreshCase();
      }
    } finally {
      setIsMarkingHandled(false);
    }
  }, [caseId, isMarkingHandled, refreshCase]);

  if (caseLoadFailed) {
    return <CaseNotFound />;
  }
  if (userStatus === "error" || currentHousehold.isError) {
    return (
      <LoadFailed
        onRetry={() => {
          if (userStatus === "error") {
            void retryUser();
          }
          if (currentHousehold.isError) {
            void currentHousehold.refetch();
          }
        }}
      />
    );
  }
  if (!user || loading || !caseData || !householdData) {
    return <LoadingLogo fullPage size="lg" />;
  }

  const isTerminalStatus = caseData.case.status === CaseStatus.CLOSED || caseData.case.status === CaseStatus.SOLVED;
  const canResidentAddReplyToTerminalCase =
    !amIBuildingOwner && (type === CaseType.WARRANTY || type === CaseType.PROPERTY_MANAGER_CASE) && isTerminalStatus;
  const showReplyComposer = amIBuildingOwner || !isTerminalStatus || showTerminalReplyComposer;

  return (
    <main className="flex flex-grow flex-col p-4 md:p-12 md:overflow-y-auto scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-white items-center">
      <div className="flex flex-grow self-stretch flex-col">
        <div className="flex flex-row pb-4">
          <ChevronLeftIcon
            className="h-9 w-9 mr-2 hover:bg-slate-300 focus-visible:bg-dobu-orange cursor-pointer rounded"
            onClick={() => navigate(-1)}
          />
          <h2 className="text-3xl font-bold text-center mr-4">
            {t(`${namespace}.cases`)} - {caseData.case.title}
          </h2>
        </div>
        <div className="flex flex-col ml-12 mt-3 pb-4">
          <h2 className="text-xl">
            <strong>{t(`${namespace}.caseStatus.status`)}: </strong>
            {amIBuildingOwner && householdData.featureFlags["buildingOwnerCanControlCaseStatus"] ? (
              <CaseStatusDropdown caseData={caseData.case} onStatusChange={handleChangeStatus} />
            ) : (
              t(caseData.case.localisedStatus)
            )}
          </h2>
          {amIBuildingOwner && (
            <>
              <h2 className="text-xl">
                <strong>{t(`${namespace}.caseDetails.address`)}: </strong>
                {householdData.building.street} - {householdData.household.title}
              </h2>
              <h2 className="text-xl">
                <strong>{t(`${namespace}.caseDetails.name`)}: </strong>
                {caseData.messages[0]?.userName}
              </h2>
              {phoneNumber && (
                <h2 className="text-xl">
                  <strong>{t(`${namespace}.caseDetails.phoneNumber`)}: </strong>
                  {phoneNumber}
                </h2>
              )}
            </>
          )}
          {caseData.case.subcontractorName && (
            <h2 className="text-xl">
              <strong>{t(`${namespace}.caseDetails.subcontractor`)}: </strong>
              {caseData.case.subcontractorName}
              {caseData.case.subcontractorEmail && (
                <span className="text-gray-500 ml-2">({caseData.case.subcontractorEmail})</span>
              )}
            </h2>
          )}
          {caseData.case.categoryName && (
            <h2 className="text-xl">
              <strong>{t(`${namespace}.caseDetails.category`)}: </strong>
              {caseData.case.categoryName}
            </h2>
          )}
        </div>
        {caseData.case.inspection && (
          <InspectionDefects
            inspection={caseData.case.inspection}
            householdId={caseData.case.householdId}
            currentCaseId={caseData.case.id}
            showExternalNumber={amIBuildingOwner}
          />
        )}
        <div className="flex flex-col xl:px-[20%]">
          <ClosableBanner
            message={t(`${namespace}.emailNotification`)}
            storageKey="warranty-email-notification-banner"
          />
          <ChatMessageHolder>
            {caseData.messages.map((message) => {
              const displayOnRight = isOwnSideMessage(
                message.accountId,
                householdData.building.ownerAccountIds,
                amIBuildingOwner,
              );
              const hasAttachment = message.type === CaseMessageType.FILE || message.type === CaseMessageType.IMAGE;
              const attachmentUrl = hasAttachment ? getDocumentUrl2(null, true, message.content) : null;
              if (message.type === CaseMessageType.AI_HISTORY && !amIBuildingOwner) {
                return <span key={message.id} />;
              }
              return (
                <ChatMessage
                  key={message.id}
                  type={displayOnRight ? ChatMessageType.QUESTION : ChatMessageType.RESPONSE}
                >
                  <div className="flex flex-col">
                    {message.type !== CaseMessageType.AI_HISTORY && (
                      <span className={clsx("text-sm text-gray-500", displayOnRight && "self-end")}>
                        {message.userName} - {message.createdAtTimestamp.format("LL")}
                      </span>
                    )}
                    {message.type === CaseMessageType.AI_HISTORY && (
                      <span className={clsx("text-sm text-gray-500", displayOnRight && "self-end")}>
                        {t("case.previousChatWithBot")}
                      </span>
                    )}
                    {message.type === CaseMessageType.TEXT && (
                      <span className={clsx(displayOnRight && "self-end", "whitespace-pre-wrap")}>
                        {message.content}
                      </span>
                    )}
                    {message.type === CaseMessageType.AI_HISTORY && (
                      <span className={clsx(displayOnRight && "self-end", "whitespace-pre-wrap text-gray-500")}>
                        {message.content}
                      </span>
                    )}
                    {message.type === CaseMessageType.IMAGE && attachmentUrl && (
                      <img
                        src={attachmentUrl}
                        alt={t("case.messageWithImage")}
                        className="max-w-[300px] max-h-[300px] md:max-w-[40vw] md:max-h-[40vh] object-contain m-0 rounded cursor-zoom-in"
                        role="button"
                        tabIndex={0}
                        onClick={() => window.open(attachmentUrl, "_blank", "noopener,noreferrer")}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            window.open(attachmentUrl, "_blank", "noopener,noreferrer");
                          }
                        }}
                      />
                    )}
                    {message.type === CaseMessageType.FILE && (
                      <ChatVideo hash={message.content} mimeType={message.mimeType} />
                    )}
                    {hasAttachment && attachmentUrl && (
                      <span
                        className={clsx(
                          "bg-slate-300 hover:bg-slate-400 cursor-pointer p-2 rounded",
                          displayOnRight && "ss",
                        )}
                        onClick={() => downloadDocument2(attachmentUrl, null, true)}
                      >
                        <ArrowDownTrayIcon className="w-4 h-4 -mt-0.5 inline mx-2" />
                        {t(`${namespace}.downloadFile`)}
                      </span>
                    )}
                  </div>
                </ChatMessage>
              );
            })}
          </ChatMessageHolder>
        </div>
        <div className="flex-grow" />
        <div className="flex flex-col xl:px-[20%] gap-3 pt-4">
          {amIBuildingOwner && isTerminalStatus && caseData.case.needsBuilderReply && (
            <div className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3 rounded-md bg-orange-50 border border-orange-200 text-orange-900 text-sm">
              <ChatBubbleLeftEllipsisIcon className="w-5 h-5 shrink-0 text-dobu-orange" />
              <span className="flex-1">{t("case.closedFollowUpBanner")}</span>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 text-dobu-orange hover:text-dobu-orange-focus"
                loading={isMarkingHandled}
                onClick={handleMarkReplyHandled}
              >
                <CheckCircleIcon className="w-4 h-4 mr-1" />
                {t("case.closeFollowUp")}
              </Button>
            </div>
          )}

          {/* Status notice */}
          {caseData.case.status === CaseStatus.CLOSED && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-gray-50 border border-gray-200 text-gray-600 text-sm">
              <LockClosedIcon className="w-5 h-5 shrink-0 text-gray-400" />
              <span>{t("case.closedByService")}</span>
            </div>
          )}
          {caseData.case.status === CaseStatus.SOLVED && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm">
              <CheckCircleIcon className="w-5 h-5 shrink-0 text-emerald-500" />
              <span>{t("case.markedAsSolved")}</span>
            </div>
          )}

          {/* Composer or reopen action */}
          {showReplyComposer ? (
            <div className="flex flex-grow relative">
              <span className="mr-4 mb-2 absolute right-0 bottom-0 flex flex-row items-center gap-2">
                <UploadFilesPopup className="flex flex-row" uploadCallback={addFiles}>
                  <PaperClipIcon className="w-6 h-6 inline text-gray-300 hover:text-dobu-orange cursor-pointer" />
                </UploadFilesPopup>

                <Button
                  type="button"
                  loading={isSending}
                  disabled={isSending || !(hasMessage || pendingFiles.length > 0)}
                  onClick={submitFilesAndMessage}
                >
                  {!isSending && <PaperAirplaneIcon className="w-4 h-4 mr-1.5 -ml-0.5" />}
                  {t("chat.send")}
                </Button>
              </span>{" "}
              <div className="flex flex-col w-full gap-2">
                {canResidentAddReplyToTerminalCase && (
                  <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                    <ChatBubbleLeftEllipsisIcon className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{t("case.replyWillStayClosed")}</span>
                  </div>
                )}
                {pendingFiles.length > 0 && (
                  <div className="flex flex-wrap border border-gray-300 rounded-md p-2 bg-white gap-2">
                    {pendingFiles.map((file, i) => (
                      <span key={i} className="rounded-md py-2 px-4 bg-gray-200">
                        <PaperClipIcon className="w-4 h-4 inline -mt-0.5 mr-2" /> {file.name}
                      </span>
                    ))}
                  </div>
                )}
                <UploadStatus progress={uploadProgress} error={sendError} />
                <textarea
                  className="flex-grow border border-gray-300 rounded-md p-2 pb-14 scrollbar-thin scrollbar-track-white scrollbar-thumb-slate-100 whitespace-pre-wrap"
                  placeholder={
                    amIBuildingOwner ? t(`${namespace}.sendMessageToOwner`) : t(`${namespace}.sendMessageToDeveloper`)
                  }
                  rows={5}
                  ref={messageContent}
                  onInput={(e) => setHasMessage(e.currentTarget.value.trim().length > 0)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                      e.preventDefault();
                      submitFilesAndMessage();
                    }
                  }}
                />
              </div>
            </div>
          ) : canResidentAddReplyToTerminalCase ? (
            <div className="rounded-lg border-2 border-dashed border-dobu-orange/40 bg-orange-50/60 p-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex-1">
                  <div className="font-semibold text-zinc-900 mb-0.5">{t("case.addReplyPromptTitle")}</div>
                  <div className="text-sm text-zinc-600">{t("case.addReplyPromptBody")}</div>
                </div>
                <Button variant="primary" size="md" onClick={openTerminalReplyComposer}>
                  <ChatBubbleLeftEllipsisIcon className="w-4 h-4 mr-1.5 -ml-0.5" />
                  {t(`${namespace}.caseActions.addReply`)}
                </Button>
              </div>
            </div>
          ) : isTerminalStatus ? (
            <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-gray-50 border border-gray-200 text-gray-500 text-sm">
              <LockClosedIcon className="w-5 h-5 shrink-0 text-gray-400" />
              <span>{t("case.closedCantMessage")}</span>
            </div>
          ) : null}
        </div>
      </div>
    </main>
  );
}
